import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Ban, Loader2, Mail, Phone, Printer, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import ContactDrawer from "./ContactDrawer";
import RescheduleControl from "./RescheduleControl";
import { ADMIN_BOOKING_STATUSES, ADMIN_BOOKING_STATUS_LABELS, BOOKING_SLOTS, todayIso } from "./labels";

const BOOKING_GOAL_LABELS = {
  general: "Non so ancora quale percorso scegliere",
  "ac-start": "AC START — Conformità",
  "ac-hunt": "AC HUNT — Acquisto",
  "ac-select": "AC SELECT — Vendita",
  "ac-invest": "AC INVEST — Investimento",
  "ac-management": "AC MANAGEMENT — Cantiere",
};
const BOOKING_PROTOCOL_OPTIONS = ["ac-start", "ac-hunt", "ac-select", "ac-invest", "ac-management", "general"];

const DAY_HEADERS = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
const MONTHS_IT = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];

function _monday_of_week(d) {
  const day = (d.getDay() + 6) % 7;
  const m = new Date(d);
  m.setDate(d.getDate() - day);
  return m;
}

function _friday_of_week(monday) {
  const f = new Date(monday);
  f.setDate(monday.getDate() + 4);
  return f;
}

// --- Gestione disponibilità: blocco manuale di slot o giornate (si riflette sulla prenotazione pubblica) ---
function AvailabilityManager({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayIso());
  const [blocks, setBlocks] = useState([]);
  const [occupied, setOccupied] = useState([]);
  const [dayBlocked, setDayBlocked] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = (d = date) => {
    if (!d) return;
    setLoading(true);
    Promise.all([
      api.get("/slot-blocks", { params: { date: d } }).catch(() => ({ data: [] })),
      api.get("/bookings/availability", { params: { date: d } }).catch(() => ({ data: { taken: [], day_blocked: false } })),
    ])
      .then(([blocksRes, availRes]) => {
        setBlocks(blocksRes.data || []);
        setOccupied(availRes.data.taken || []);
        setDayBlocked(!!availRes.data.day_blocked);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutate = async (fn) => {
    try {
      await fn();
      load();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const blockFor = (time) => blocks.find((b) => (b.time || null) === (time || null));
  const booked = (time) => !dayBlocked && occupied.includes(time) && !blockFor(time);

  const toggleDay = () =>
    mutate(async () => {
      const b = blockFor(null);
      if (b) await api.delete(`/slot-blocks/${b.id}`);
      else await api.post("/slot-blocks", { date });
    });

  const toggleSlot = (time) =>
    mutate(async () => {
      const b = blockFor(time);
      if (b) await api.delete(`/slot-blocks/${b.id}`);
      else await api.post("/slot-blocks", { date, time });
    });

  return (
    <div className="border border-[#c9a45c]/25 bg-[#101010] p-4" data-testid="availability-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-gold-light">Gestione disponibilità</p>
        <button
          onClick={() => setOpen((v) => !v)}
          className="btn btn-outline !min-h-[36px] !px-4 !text-[10px]"
          data-testid="availability-toggle"
        >
          {open ? "Chiudi" : "Gestisci slot"}
        </button>
      </div>
      {open && (
        <div className="mt-4" data-testid="availability-editor">
          <p className="text-[11px] leading-relaxed text-[#929292]">
            Gli slot e le giornate bloccati appaiono occupati anche nella prenotazione pubblica del sito.
            Le prenotazioni già confermate restano invariate.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="text-[11px] text-[#c9c6bf]">
              <span className="mb-1 block text-[9px] uppercase tracking-[0.14em] text-[#a8a5a0]">Giornata</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="field !w-auto !py-2 !text-[12px]"
                data-testid="availability-date-input"
              />
            </label>
            <button
              onClick={toggleDay}
              disabled={loading}
              className={`btn !min-h-[36px] !px-4 !text-[10px] ${dayBlocked ? "btn-gold" : "btn-outline"}`}
              data-testid="availability-day-block"
            >
              <Ban size={12} /> {dayBlocked ? "Giornata bloccata: sblocca" : "Blocca l'intera giornata"}
            </button>
            {loading && <Loader2 size={14} className="animate-spin text-gold" />}
          </div>
          {dayBlocked ? (
            <p className="mt-3 text-[11px] text-[#929292]" data-testid="availability-day-note">
              Tutta la giornata del {date.split("-").reverse().join("/")} è bloccata: nessun orario prenotabile.
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="availability-slot-grid">
              {BOOKING_SLOTS.map((s) => {
                const isBooked = booked(s);
                const isBlocked = !!blockFor(s);
                return (
                  <button
                    key={s}
                    onClick={() => !isBooked && toggleSlot(s)}
                    disabled={isBooked || loading}
                    className={`border px-2.5 py-1.5 text-[11px] transition-colors duration-150 ${
                      isBooked
                        ? "cursor-not-allowed border-[#c9a45c]/15 bg-[#c9a45c]/5 text-[#5c5c5c]"
                        : isBlocked
                          ? "border-[#e07a5f]/50 bg-[#e07a5f]/10 text-[#e07a5f] line-through"
                          : "border-[#c9a45c]/35 text-[#c9c6bf] hover:border-gold hover:text-gold-light"
                    }`}
                    data-testid={`availability-slot-${s}`}
                  >
                    {s} {isBooked ? "· occupato" : isBlocked ? "· bloccato" : ""}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Calendario settimanale con chip prenotazioni (clic = scheda cliente) e blocchi ---
function CalendarChip({ booking, onOpenContact }) {
  const annullata = booking.status === "annullata";
  const clickable = !!booking.contact_id && !annullata;
  const content = (
    <>
      <span className="shrink-0 font-bold text-gold-light">{booking.time}</span>
      <span className="truncate">{booking.name}</span>
      {booking.meet_link && !annullata && <Video size={11} className="ml-auto shrink-0 text-gold" />}
    </>
  );
  const cls = `flex items-center gap-2 border px-2.5 py-2 text-[11px] leading-tight ${
    annullata
      ? "border-[#c9a45c]/12 text-[#5c5c5c] line-through"
      : booking.status === "svolta"
        ? "border-[#c9a45c]/25 bg-[#c9a45c]/5 text-[#929292]"
        : "border-[#d4af37]/60 bg-[#d4af37]/10 text-cream"
  } ${clickable ? "w-full text-left transition-colors hover:border-gold" : ""}`;
  if (clickable) {
    return (
      <button onClick={() => onOpenContact(booking.contact_id)} className={cls} data-testid={`booking-chip-${booking.id}`}>
        {content}
      </button>
    );
  }
  return (
    <div className={cls} data-testid={`booking-chip-${booking.id}`}>
      {content}
    </div>
  );
}

function CalendarWeek({ bookings, onOpenContact, refreshKey }) {
  const [weekStart, setWeekStart] = useState(() => _monday_of_week(new Date()));
  const [blocksByDay, setBlocksByDay] = useState({});

  useEffect(() => {
    const from = weekStart.toISOString().slice(0, 10);
    const to = _friday_of_week(weekStart).toISOString().slice(0, 10);
    api
      .get("/slot-blocks", { params: { from_date: from, to_date: to } })
      .then(({ data }) => {
        const map = {};
        (data || []).forEach((b) => {
          (map[b.date] = map[b.date] || []).push(b);
        });
        setBlocksByDay(map);
      })
      .catch(() => setBlocksByDay({}));
  }, [weekStart, refreshKey]);

  const days = useMemo(() => {
    const list = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      list.push({
        iso: d.toISOString().slice(0, 10),
        header: `${DAY_HEADERS[i]} ${String(d.getDate()).padStart(2, "0")} ${MONTHS_IT[d.getMonth()]}`,
      });
    }
    return list;
  }, [weekStart]);

  const byDay = useMemo(() => {
    const map = {};
    (bookings || []).forEach((b) => {
      (map[b.date] = map[b.date] || []).push(b);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => (a.time || "").localeCompare(b.time || "")));
    return map;
  }, [bookings]);

  const weekLabel = `${String(weekStart.getDate()).padStart(2, "0")} ${MONTHS_IT[weekStart.getMonth()]} — ${String(
    new Date(weekStart.getTime() + 4 * 86400000).getDate()
  ).padStart(2, "0")} ${MONTHS_IT[new Date(weekStart.getTime() + 4 * 86400000).getMonth()]} ${new Date(
    weekStart.getTime() + 4 * 86400000
  ).getFullYear()}`;

  const printToday = () => {
    const esc = (s) =>
      String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const todayIsoStr = new Date().toLocaleDateString("en-CA");
    const list = (byDay[todayIsoStr] || []).filter((b) => b.status === "confermata");
    const label = todayIsoStr.split("-").reverse().join("/");
    const rows = list
      .map(
        (b) => `<tr>
          <td style="padding:8px 10px;border:1px solid #ddd;font-weight:bold;white-space:nowrap">${esc(b.time)}</td>
          <td style="padding:8px 10px;border:1px solid #ddd">${esc(b.name)}</td>
          <td style="padding:8px 10px;border:1px solid #ddd;white-space:nowrap">${esc(b.phone)}</td>
          <td style="padding:8px 10px;border:1px solid #ddd">${esc(BOOKING_GOAL_LABELS[b.goal] || b.goal)}</td>
          <td style="padding:8px 10px;border:1px solid #ddd;font-size:11px">${esc(b.situation)}</td>
        </tr>`
      )
      .join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Appuntamenti ${label} — A.C. HUB</title></head>
      <body style="font-family:Arial,sans-serif;color:#111;margin:24px">
        <p style="letter-spacing:3px;color:#b8952f;font-weight:bold;font-size:11px;margin:0 0 4px">A.C. HUB — REAL ESTATE ADVISORY</p>
        <h1 style="font-size:20px;margin:0 0 2px">Giornata del ${label}</h1>
        <p style="color:#666;font-size:12px;margin:0 0 16px">${list.length} Analisi Strategiche confermate</p>
        ${
          list.length
            ? `<table style="border-collapse:collapse;width:100%;font-size:13px">
          <thead><tr><th align="left" style="padding:8px 10px;border:1px solid #999;background:#f5f0e4">ORA</th>
          <th align="left" style="padding:8px 10px;border:1px solid #999;background:#f5f0e4">CLIENTE</th>
          <th align="left" style="padding:8px 10px;border:1px solid #999;background:#f5f0e4">TELEFONO</th>
          <th align="left" style="padding:8px 10px;border:1px solid #999;background:#f5f0e4">OBIETTIVO</th>
          <th align="left" style="padding:8px 10px;border:1px solid #999;background:#f5f0e4">SITUAZIONE</th></tr></thead>
          <tbody>${rows}</tbody></table>`
            : `<p style="color:#666">Nessuna Analisi Strategica confermata per oggi.</p>`
        }
        <p style="color:#999;font-size:10px;margin-top:24px">Documento interno riservato — A.C. HUB. Non distribuire.</p>
      </body></html>`;
    const w = window.open("", "_blank", "width=900,height=650");
    if (!w) {
      toast.error("Consenti i popup per stampare la giornata");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div className="mt-6" data-testid="admin-calendar">
      <div className="flex flex-wrap items-center gap-2" data-testid="calendar-nav">
        <button
          data-testid="calendar-week-prev"
          onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(w.getDate() - 7); return n; })}
          className="border border-[#c9a45c]/25 px-3 py-2 text-[10px] font-bold tracking-[0.16em] text-[#929292] transition-colors hover:text-cream"
        >
          <ArrowLeft size={12} className="inline" /> SETTIMANA
        </button>
        <button
          data-testid="calendar-week-today"
          onClick={() => setWeekStart(_monday_of_week(new Date()))}
          className="border border-[#c9a45c]/25 px-3 py-2 text-[10px] font-bold tracking-[0.16em] text-[#929292] transition-colors hover:text-cream"
        >
          OGGI
        </button>
        <button
          data-testid="calendar-week-next"
          onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(w.getDate() + 7); return n; })}
          className="border border-[#c9a45c]/25 px-3 py-2 text-[10px] font-bold tracking-[0.16em] text-[#929292] transition-colors hover:text-cream"
        >
          SETTIMANA <span className="inline-block rotate-180"><ArrowLeft size={12} /></span>
        </button>
        <span className="ml-2 text-[12px] font-semibold text-gold-light" data-testid="calendar-week-label">{weekLabel}</span>
        <button
          data-testid="calendar-print-day"
          onClick={printToday}
          className="ml-auto border border-[#c9a45c]/25 px-3 py-2 text-[10px] font-bold tracking-[0.16em] text-[#929292] transition-colors hover:text-cream"
        >
          <Printer size={12} className="inline" /> STAMPA OGGI
        </button>
      </div>

      {bookings === null && (
        <div className="mt-4 flex items-center gap-3 border border-[#c9a45c]/18 bg-[#101010] p-6 text-[13px] text-[#929292]">
          <Loader2 size={14} className="animate-spin text-gold" /> Caricamento calendario…
        </div>
      )}

      {bookings !== null && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-5" data-testid="calendar-week-grid">
          {days.map((d) => (
            <div key={d.iso} className="border border-[#c9a45c]/18 bg-[#101010] p-3" data-testid={`calendar-day-${d.iso}`}>
              <p className="border-b border-[#c9a45c]/18 pb-2 text-center text-[10px] font-bold tracking-[0.14em] text-gold-light">
                {d.header}
              </p>
              <div className="mt-3 grid min-h-[120px] content-start gap-2">
                {(byDay[d.iso] || []).map((b) => (
                  <CalendarChip key={b.id} booking={b} onOpenContact={onOpenContact} />
                ))}
                {(blocksByDay[d.iso] || []).map((bl) => (
                  <div key={bl.id} className="flex items-center gap-2 border border-[#5c5c5c]/40 bg-[#141414] px-2.5 py-2 text-[10px] uppercase tracking-[0.1em] text-[#777]" data-testid={`block-chip-${bl.id}`}>
                    <Ban size={10} /> {bl.time ? `Bloccato ${bl.time}` : "Giornata bloccata"}
                  </div>
                ))}
                {!(byDay[d.iso] || []).length && !(blocksByDay[d.iso] || []).length && (
                  <p className="py-6 text-center text-[11px] text-[#5c5c5c]">Nessuna sessione</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Elenco prenotazioni con filtri prossime/passate ---
function BookingRow({ booking, onStatus, onGoal, onUpdated, onOpenContact }) {
  const [rescheduling, setRescheduling] = useState(false);
  return (
    <div className="border border-[#c9a45c]/18 bg-[#101010] p-5" data-testid="booking-row">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-cream" data-testid={`booking-name-${booking.id}`}>
            {booking.name}
            {booking.source && (
              <span
                className="ml-2 border border-[#c9a45c]/30 px-2 py-0.5 align-middle text-[9px] font-bold uppercase tracking-[0.14em] text-gold-light"
                data-testid={`booking-source-${booking.id}`}
              >
                {(booking.source || "").replace(/-/g, " ")}
              </span>
            )}
          </p>
          <p className="mt-1 font-serif text-lg text-gold-light" data-testid={`booking-slot-${booking.id}`}>
            {String(booking.date || "").split("-").reverse().join("/")} · ore {booking.time}
          </p>
          <p className="mt-1 text-[12px] uppercase tracking-[0.1em] text-gold-light" data-testid={`booking-goal-${booking.id}`}>
            {BOOKING_GOAL_LABELS[booking.goal] || booking.goal}
          </p>
        </div>
        <select
          className="field !w-auto !py-2 !text-[11px]"
          value={booking.status}
          onChange={(e) => onStatus(booking.id, e.target.value)}
          data-testid={`booking-status-select-${booking.id}`}
        >
          {ADMIN_BOOKING_STATUSES.map((s) => (
            <option key={s} value={s}>{ADMIN_BOOKING_STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-[#c9c6bf]">
        {booking.phone && (
          <a href={`tel:${booking.phone}`} className="inline-flex items-center gap-1.5 hover:text-gold-light">
            <Phone size={12} className="text-gold" /> {booking.phone}
          </a>
        )}
        {booking.email && (
          <a href={`mailto:${booking.email}`} className="inline-flex items-center gap-1.5 hover:text-gold-light">
            <Mail size={12} className="text-gold" /> {booking.email}
          </a>
        )}
        {booking.meet_link && (
          <a
            href={booking.meet_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-gold-light hover:underline"
            data-testid={`booking-meet-${booking.id}`}
          >
            <Video size={12} className="text-gold" /> Entra nella video-call
          </a>
        )}
        {booking.contact_id && (
          <button
            onClick={() => onOpenContact(booking.contact_id)}
            className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] underline-offset-2 hover:text-gold-light hover:underline"
            data-testid={`booking-open-contact-${booking.id}`}
          >
            Apri scheda cliente
          </button>
        )}
        {booking.status !== "annullata" && (
          <button
            onClick={() => setRescheduling((v) => !v)}
            className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] underline-offset-2 hover:text-gold-light hover:underline"
            data-testid={`booking-reschedule-${booking.id}`}
          >
            Riprogramma
          </button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#a8a5a0]">Protocollo consigliato</span>
        <select
          className="field !w-auto !py-1.5 !text-[11px]"
          value={booking.goal || "general"}
          onChange={(e) => onGoal(booking.id, e.target.value)}
          data-testid={`booking-goal-select-${booking.id}`}
        >
          {BOOKING_PROTOCOL_OPTIONS.map((g) => (
            <option key={g} value={g}>{BOOKING_GOAL_LABELS[g] || g}</option>
          ))}
        </select>
      </div>
      {rescheduling && (
        <RescheduleControl
          booking={booking}
          onDone={(data) => {
            setRescheduling(false);
            onUpdated(data);
          }}
          onCancel={() => setRescheduling(false)}
        />
      )}
      {(booking.selected_goal || booking.selected_type) && (
        <p className="mt-3 max-w-[620px] text-[13px] leading-relaxed text-[#dedbd4]">
          {booking.selected_goal}
          {booking.selected_type ? ` · ${booking.selected_type}` : ""}
        </p>
      )}
      {booking.situation && (
        <p className="mt-2 max-w-[620px] text-[13px] leading-relaxed text-[#bdb8ad]">"{booking.situation}"</p>
      )}
    </div>
  );
}

function BookingList({ bookings, onStatus, onGoal, onUpdated, onOpenContact }) {
  const [when, setWhen] = useState("future");
  const today = todayIso();

  const filtered = (bookings || []).filter((b) => {
    if (when === "future") return (b.date || "") >= today;
    if (when === "past") return (b.date || "") < today;
    return true;
  });

  return (
    <div className="mt-8" data-testid="admin-bookings-list">
      <div className="flex flex-wrap items-center gap-2" data-testid="agenda-filters">
        <p className="mr-2 text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">Elenco prenotazioni</p>
        {[
          { v: "future", l: "PROSSIME", t: "agenda-filter-future" },
          { v: "past", l: "PASSATE", t: "agenda-filter-past" },
          { v: "all", l: "TUTTE", t: "agenda-filter-all" },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={f.t}
            onClick={() => setWhen(f.v)}
            className={`border px-4 py-2 text-[9px] font-bold tracking-[0.16em] transition-colors duration-200 ${
              when === f.v ? "border-gold bg-[#c9a45c]/12 text-gold-light" : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>
      <div className="mt-4 grid gap-3">
        {bookings === null && (
          <div className="flex items-center gap-3 border border-[#c9a45c]/18 bg-[#101010] p-6 text-[13px] text-[#929292]">
            <Loader2 size={14} className="animate-spin text-gold" /> Caricamento prenotazioni…
          </div>
        )}
        {bookings !== null && filtered.length === 0 && (
          <div className="border border-[#c9a45c]/18 bg-[#101010] p-8 text-center text-[13px] text-[#929292]">
            Nessuna prenotazione in questa vista, per ora.
          </div>
        )}
        {filtered.map((b) => (
          <BookingRow key={b.id} booking={b} onStatus={onStatus} onGoal={onGoal} onUpdated={onUpdated} onOpenContact={onOpenContact} />
        ))}
      </div>
    </div>
  );
}

// --- Scheda AGENDA: un solo caricamento dati condiviso da calendario, elenco e disponibilità ---
export default function AgendaPanel() {
  const [bookings, setBookings] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openContactId, setOpenContactId] = useState(null);

  useEffect(() => {
    api
      .get("/bookings")
      .then(({ data }) => setBookings(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [refreshKey]);

  const updateBookingData = (data) => {
    setBookings((bs) => (bs || []).map((b) => (b.id === data.id ? data : b)));
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      updateBookingData(data);
      toast.success("Stato aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateGoal = async (id, goal) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { goal });
      updateBookingData(data);
      toast.success("Protocollo consigliato aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="pb-16" data-testid="agenda-panel">
      <AvailabilityManager onChanged={() => setRefreshKey((k) => k + 1)} />
      <CalendarWeek bookings={bookings} onOpenContact={setOpenContactId} refreshKey={refreshKey} />
      <BookingList
        bookings={bookings}
        onStatus={updateStatus}
        onGoal={updateGoal}
        onUpdated={updateBookingData}
        onOpenContact={setOpenContactId}
      />
      {openContactId && (
        <ContactDrawer
          contactId={openContactId}
          onClose={() => setOpenContactId(null)}
          onChanged={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
